import React, { memo } from 'react';
import { DateTime } from 'luxon';
import { Link } from '@mui/material';
import Date from 'components/formaters/Date';
import { MessageModel } from 'models/ChatModel';
import { sanitizeUrl } from 'utils/common';


type MessageProps = {
    message: MessageModel,
    mine: boolean
    prev?: MessageModel,
}

export const MessageItem = memo(({ message, mine, prev }: MessageProps) => {
    let divider = null;

    const curDateTime = DateTime.fromISO(message.createdAt);

    if (!prev || curDateTime.toISODate() !== DateTime.fromISO(prev.createdAt).toISODate()) {
        divider = (
            <div className="divider">
                <Date date={curDateTime}/>
            </div>
        )
    }

    const sameAuthor = prev?.author.id === message.author.id;
    const formatted = formatText(message.text);

    return (
        <>
            {divider}

            <div className={`message ${mine ? 'mine' : 'their'} ${message.unread ? 'm-unread' : ''}`}>
                <div className="header">
                    {(mine || sameAuthor) ? <div /> : <strong>{message.author.name}</strong>}
                    <span className="time">
                        {curDateTime.toLocaleString({hour: '2-digit', minute: '2-digit'})}
                    </span>
                </div>
                {formatted}

                <div className="attachments">
                    {message.attachments?.map(attachment => (
                        <a
                            key={attachment.id}
                            href={attachment.content}
                            className="attachment"
                            target="_blank"
                            rel="noreferrer"
                        >
                            <img alt="" src={attachment.preview} />
                        </a>
                    ))}
                </div>
            </div>
        </>
    )
}, (prev, next) => {
    return prev.message.text === next.message.text;
});

const formatText = (text: string) => {
    let temp = [];

    // find Markdown link
    if (text.includes('](')) {
        const links = [...text.matchAll(/\[(.+?)]\(((?:\/|https?:\/\/)[\w./?=#&-]+)\)/g)];
        let lastCursor = 0;

        links.forEach(link => {
            const [ full, title, url ] = link;

            if (link.index! > 0) {
                temp.push(text.slice(lastCursor, link.index))
            }

            temp.push(<Link key={`${link.index}-${link.index! + full.length}`} href={sanitizeUrl(url)}>{title}</Link>)
            lastCursor = link.index! + full.length;
        });

        if (lastCursor < text.length) {
            temp.push(text.slice(lastCursor))
        }
    } else {
        // find raw link
        const links = [...text.matchAll(/https?:\/\/[\w.:/?=#&-]+/g)];

        if (links.length) {
            let lastCursor = 0;

            links.forEach(link => {
                const [ url ] = link;

                if (link.index! > 0) {
                    temp.push(text.slice(lastCursor, link.index))
                }

                if (url.startsWith(window.location.origin)) {
                    const local = url.replace(window.location.origin, '')
                    temp.push(<Link
                        key={`${link.index}-${link.index! + url.length}`}
                        href={sanitizeUrl(local)}
                    >
                        {local}
                    </Link>)
                } else {
                    temp.push(<a
                        key={`${link.index}-${link.index! + url.length}`}
                        href={sanitizeUrl(url)}
                        target="_blank"
                        rel="noreferrer"
                    >
                        {url}
                    </a>)
                }

                lastCursor = link.index! + url.length;
            });

            if (lastCursor < text.length) {
                temp.push(text.slice(lastCursor))
            }
        }
    }

    if (!temp.length) { // if no links found show whole text
        temp.push(text)
    }

    return temp;
};

